// Content types and data for smartQ marketing site
// Source: CONTENT_LIBRARY from specification

export type EtapeItem = {
  title?: string
  subtitle?: string
  content: string
}

export type UseCaseItem = {
  title: string
  description: string
}

export type BenefitCategory = {
  category: string;
  items: string[];
};

export type PageSection = {
  title: string;
  purpose?: string;
  hero?: {
    headline: string;
    sub?: string;
    ctaPrimary: string;
    ctaSecondary?: string;
  };
  benefits?: string[] | BenefitCategory[];
  steps?: (string | EtapeItem)[];
  useCases?: (string | UseCaseItem)[];
  comparison?: { aspect: string; physical: string; virtual: string }[];
  indispensable?: { title: string; description: string }[];
  proof?: string[];
  objections?: { claim: string; reply: string }[];
  compliance?: string[];
  implementation?: string[];
  cta?: { primary: string; secondary?: string };
  internalLinks?: string[];
  visuals?: string[];
  seo?: { meta: string };
  video?: {
    title: string;
    description: string;
    videoId: string;
  };
};

export type SiteContent = {
  pages: Record<string, PageSection>;
  checklist: string[];
  openQuestions: string[];
};

export const content: SiteContent = {
  pages: {
    home: {
      title: "Home / Overview",
      purpose: "Queues have negative impact on the experience and satisfaction of your customers. Show why smartQ matters: happier customers, smoother ops, fast ROI via a Virtual Queue — 'waiting without queuing'.",
      hero: {
        headline: "The best way of queueing—without the queue.",
        sub: "Transform your queue into a virtual queue to retain and attract more customers. Quick to set up, easy to run, loved by customers.",
        ctaPrimary: "Start free trial",
        ctaSecondary: "Book a demo"
      },
      benefits: [
        "**Cut visible lines**. Move waiting off‑site with a Simple and Easy Queue Management System.",
        "**Happier customers**. Real‑time updates reduce stress and perceived wait.",
        "**Faster flow**. Minimize wait times and reduce crowds in your space.",
        "**Quick setup, low cost**. Inexpensive, customizable, quick setup.",
        "**Compatible with your traditional queue system**"
      ],
      steps: [
        "Customers join from a link/QR or on‑site kiosk.",
        "smartQ confirms their place and sends live updates.",
        "When it's time, they're notified to come forward."
      ],
      video: {
        title: "How it works",
        description: "See how smartQ works in action",
        videoId: "1c9bd4c589f04dfd85da03d2ce11479e"
      },
      comparison: [
        { aspect: "Joining", physical: "Stand in line / paper ticket", virtual: "Scan QR / tap link / staff adds" },
        { aspect: "Updates", physical: "Ask staff / watch screens", virtual: "Real-time SMS/email/push" },
        { aspect: "Wait Experience", physical: "Feels long & uncertain", virtual: "Transparent & shorter perception" },
        { aspect: "No-shows", physical: "Higher, hard to manage", virtual: "Easy cancellation & delays" },
        { aspect: "Operations", physical: "Unpredictable walk-ins", virtual: "Analytics for staff planning" },
        { aspect: "Branding", physical: "Generic ticket", virtual: "Branded digital experience" }
      ],
      useCases: [
        "Retail click & collect",
        "Clinics and practices",
        "Barbers and salons",
        "Public services counters"
      ],
      proof: [
        "Quick setup and customizable configuration referenced in intro.",
        "Less crowding when customers wait remotely.",
        "[Stat needed: \"Avg. visible wait line ↓ by X%; CSAT ↑ by Y points.\"]"
      ],
      objections: [
        { claim: "Customers won't download an app.", reply: "They can scan a QR or use a simple link — no app required." },
        { claim: "Change will be hard.", reply: "Setup is quick; UI is simple; support is available." },
        { claim: "Is it compatible with my traditional queue at the same time?", reply: "Absolutely, you can assign positions and track progress on an on-site screen." },
        { claim: "My business is too small.", reply: "smartQ is inexpensive and runs on devices you already own." }
      ],
      compliance: [
        "Use minimal contact info; show clear consent for notifications.",
        "Display privacy notice; support accessibility and multilingual UI."
      ],
      implementation: [
        "Go live quickly using a TV/tablet/phone as a display.",
        "Simple admin panel; remote support available."
      ],
      internalLinks: ["benefits", "features", "pricing"],
      visuals: [
        "Hero image: customer holding a phone with digital ticket",
        "Diagram: Physical line vs Virtual queue flow"
      ],
      seo: { meta: "Replace lines with freedom. smartQ is the Virtual Queue that cuts wait times and crowds with simple, quick setup." }
    },

    benefits: {
      title: "Benefits (Pain Points Solved)",
      purpose: "Spell out ROI: more sales, smoother service, lower costs — without complexity.",
      hero: {
        headline: "Turn queues into sales, not stress.",
        sub: "Smart, virtual waiting that respects time, boosts loyalty, and optimizes staff.",
        ctaPrimary: "Start free trial",
        ctaSecondary: "Book a demo"
      },
      benefits: [
        {
          category: "Revenue",
          items: [
            "Higher Revenue : Reduced abandonment rates mean serving more customers and capturing sales that would otherwise walk away.",
            "Increase conversions & loyalty. Long lines cause walk‑outs; structured virtual queues keep customers engaged.",
            "Competitive Advantage: stand out and become the preferred choice"
          ]
        },
        {
          category: "Productivity",
          items: [
            "Better distributed attendance. Customers check wait times and choose the best moment to visit.",
            "Plan staffing smarter. Use queue data to align people with demand.",
            "Reduce congestion. Manage foot traffic and health risks; visitors can come at off‑peak times.",
            "Lower no‑shows. Postpone/cancel options and reminders reduce missed turns.",
            "Happier teams. Less line wrangling means better service.",
            "Improved service quality. Staff can focus on customers rather than managing physical lines.",
            "Optimized use of physical spaces. Virtual queues reduce the need for large waiting areas and improve space utilization."
          ]
        },
        {
          category: "Customer Experience",
          items: [
            "Faster perceived wait times. Virtual queues make waiting feel shorter and more manageable."
          ]
        }
      ],
      steps: [
        "Customers see wait time upfront and join digitally.",
        "smartQ sends updates; customers adjust plans if needed.",
        "Staff view live demand and serve in order — fair and fast."
      ],
      useCases: [
        "Peak evenings/weekends: avoid chaos with orderly digital lines.",
        "Service segmentation: separate queues for different services.",
        "Customer database building: collect opt‑in contacts to re‑engage."
      ],
      indispensable: [
        {
          title: "Peak Demand Management",
          description: "Transform peak traffic into controlled opportunities. Automatically distribute customer flow to off-peak periods using real-time wait time estimates. Eliminate bottlenecks by informing customers of the best time to arrive, maintaining consistent service rhythm even during peak hours."
        },
        {
          title: "Walk-outs and No-shows",
          description: "Reduce abandonment rates as virtual waiting is better accepted. Offer postponement or cancellation options."
        },
        {
          title: "Omnichannel Journey (Walk-in + Appointments + Online)",
          description: "Unify service order and synchronize all entry channels."
        },
        {
          title: "Variable Service Durations",
          description: "Minimize downtime between short and long service interventions."
        },
        {
          title: "Limited Waiting Space",
          description: "Move waiting off-site for enhanced comfort and safety."
        },
        {
          title: "Multiple Queues and Priorities (Sorting, Emergencies, VIP)",
          description: "Orchestrate service order efficiently."
        },
        {
          title: "Fluctuating Staff Resources",
          description: "Align service throughput with actual staff presence and scheduling challenges."
        },
        {
          title: "Data-Driven Operations",
          description: "Adjust resources to demand by service in real-time."
        },
        {
          title: "High-Uncertainty Events and Launches",
          description: "Absorb demand waves without operational chaos."
        },
        {
          title: "Flawless Customer Experience",
          description: "You truly value their time: waiting is perceived better, more serene. Satisfaction and trust increase, along with brand preference and retention."
        }
      ],
      proof: [
        "Prioritize by need/urgency to keep special cases moving.",
        "Analytics inform peak hours and service durations.",
        "40% fewer Walk-outs. Customers stay when they can handle wait times.",
      ],
      objections: [
        { claim: "Demand is unpredictable.", reply: "Live queue data shows surges early; redeploy staff quickly." },
        { claim: "People won't share numbers.", reply: "Offer anonymous tokens or printed tickets; tracking is optional." },
        { claim: "Manual lists are enough.", reply: "smartQ provides accurate wait estimates and proactive alerts." }
      ],
      compliance: [
        "Transparent data use; opt‑in for SMS/email; equitable service order."
      ],
      implementation: [
        "Run on existing devices; configure queues in a simple dashboard.",
        "Use QR codes, links, or kiosk; staff learn it in minutes."
      ],
      internalLinks: ["journey", "features", "pricing"],
      visuals: [
        "Chart: abandonment rate before/after",
        "Heatmap: peak hours"
      ],
      seo: { meta: "Stop losing sales to long lines. smartQ's Virtual Queue reduces crowding, boosts loyalty, and optimizes staffing." }
    },

    features: {
      title: "Product / Features",
      purpose: "Show the key features that make smartQ Virtual queue practical from day one: real-time information, automatic notifications, analytics and an easy dashboard.",
      hero: {
        headline: "Advanced features. Simple usage.",
        sub: "Real-time updates, phone and screen notifications — no special hardware required.",
        ctaPrimary: "Start free trial",
        ctaSecondary: "Book a demo"
      },
      benefits: [
        "Real-time transparency. Live position and estimated wait time updates for better time management.",
        "Multi-channel automatic notifications. Alerts when their turn approaches (SMS, email or push).",
        "Analytics and reports. Wait metrics, peak hours and bottlenecks for better decisions.",
        "Branding and multilingual. Custom name, texts and logo; multi-language support.",
        "Admin web & mobile. Efficiently manage queues through an intuitive administrative interface.",
        "Queue open/close scheduling. Easily manage queue opening and closing times based on your operational needs."
      ],
      steps: [
        "Customer joins via QR or link and receives their token.",
        "smartQ updates progress and sends notifications when their turn approaches.",
        "Staff manages from dashboard and calls next (visual/audio alerts)."
      ],
      useCases: [
        "Pre-entry: customer can join before arriving.",
        "Delay visit: if running late, add buffer and keep place.",
        "On-site display: show \"who's next\" on TV, tablet or mobile."
      ],
      proof: [
        "Updates and notifications reduce uncertainty and improve experience.",
        "Analytics for staffing and marketing (waits, peak hours, flow).",
        "[Stat needed: \"Average wait ↓ X%; NPS ↑ Y pts in 30 days.\"]"
      ],
      objections: [
        { claim: "Do I need special hardware?", reply: "No. Works with equipment you already have; web/mobile access." },
        { claim: "Does it fit my brand?", reply: "Yes. Customize logo, texts and languages." },
        { claim: "What if I have a problem?", reply: "Direct support from technical team when needed." }
      ],
      compliance: [
        "Offer paper ticket option for customers without mobile or who don't want to share data; show consent for notifications and privacy policy."
      ],
      implementation: [
        "Simple setup: create queues, print QR and start. Intuitive panel; accessed from mobile or web with secure login. Remote support available."
      ],
      internalLinks: ["journey", "implementation", "pricing"],
      visuals: [
        "Hero: digital ticket and live status on smartphone.",
        "Graphic: control panel with queues and times; notification mockups."
      ],
      seo: { meta: "smartQ features: live updates, multi-channel notifications, analytics and web/mobile panel — easy and effective Virtual queue." }
    },

    journey: {
      title: "Visitor Journey",
      purpose: "Make the experience tangible: what visitors see, do, and feel.",
      hero: {
        headline: "Take a ticket—then take your time.",
        sub: "Guests join by QR or link, see their status live, and arrive just in time.",
        ctaPrimary: "Start free trial",
        ctaSecondary: "Book a demo"
      },
      benefits: [
        "**Simplicity** : Scan the QR code to join the queue in 5 seconds",
        "**Flexibility** : Check wait times and choose to join now or later. Chosen wait instead of endured wait.",
        "**Prioritization** : Ability to prioritize customers with special needs or in emergency situations",
        "**Memorable experience** : Creates a positive and lasting relationship with every visit through a simple, fluid and frictionless journey.",
        "**Smooth communication of delays** : Clearly informs about expected waiting time, making the experience more organized and reassuring for the customer.",
        "**Precision of estimates** : Improves the accuracy of delays through real-time occupancy tracking, for realistic and reliable waiting times.",
        "**Reduced perceived waiting** : An informed customer is more tolerant: the perception of waiting often decreases when information is transparent—the feeling aligns with reality.",
        "**Transparency that inspires confidence** : Eliminates doubt (\"Very often, the perceived waiting time is higher than the actual one…\") by providing clear and shared data.",
        "**Respect for customer time** : Shows that the business values customers' time, which strengthens satisfaction and loyalty.",
        "**Sustainable competitive advantage** : Companies that invest in customer experience outperform their competitors in retention, recommendation and revenue."
      ],
      steps: [
        "Get your token via QR/link.",
        "Track progress with real‑time updates.",
        "It's your turn — come forward to be served."
      ],
      useCases: [
        "Busy barbers: quick joins and relaxed waits.",
        "Clinics: fewer crowded rooms, just‑in‑time arrival.",
        "Customer service desks: smooth, fair order."
      ],
      proof: [
        "Scan‑to‑join journey documented with screenshots.",
        "[Stat needed: \"Perceived wait ↓ by X%; walk‑outs ↓ by Y% after rollout.\"]",
      ],
      objections: [
        { claim: "What about non-tech customers?", reply: "Staff can issue paper tickets while customers follow progress on screens." },
        { claim: "Some guests don't use smartphones.", reply: "Provide a written/printed ticket and manage their place on the display." },
        { claim: "What if they wander too far?", reply: "Reminders + countdown alerts bring them back on time." }
      ],
      compliance: [
        "Readable instructions (large type, high contrast).",
        "Non‑digital alternative supported."
      ],
      implementation: [
        "Print your QR and display it at entry; optional on‑site screen shows who's next."
      ],
      internalLinks: ["features", "implementation"],
      visuals: [
        "Storyboard: Scan → Token → Updates → Serve",
        "Diagram: Physical vs Virtual path"
      ],
      seo: { meta: "See the Visitor Journey: scan a QR, watch your place live, and arrive just in time—Waiting without queuing." }
    },

    implementation: {
      title: "Implementation & Support",
      purpose: "Show that going live is straightforward with help on hand.",
      hero: {
        headline: "Go live fast with what's on your counter.",
        sub: "Set up queues, print a QR, use a TV/tablet/phone as a display. We're here if you need us.",
        ctaPrimary: "Start free trial",
        ctaSecondary: "Book a demo"
      },
      benefits: [
        "Straightforward setup. User‑friendly with docs and support.",
        "Flexible displays. TV, tablet, or phone.",
        "Ongoing help. Direct access to a tech support team."
      ],
      steps: [
        "Create queues and branding.",
        "Generate and post your QR/link; optional on‑site display.",
        "Invite staff; start serving with live updates."
      ],
      useCases: [
        "Single‑site stores, clinics, salons can launch within a day using existing devices."
      ],
      proof: [
        "Emphasis on simple setup and reliable support.",
        "[Stat needed: \"Avg. time to launch: X hours; training time: Y minutes.\"]",
      ],
      objections: [
        { claim: "Is it difficult to implement?", reply: "Admin interface simple with remote team assistance when needed. Works on devices you already own." },
        { claim: "We're not technical.", reply: "Intuitive admin; support available remotely." },
        { claim: "Do we need special hardware?", reply: "No; standard devices work." },
        { claim: "What if something breaks?", reply: "Here to help when you need us." }
      ],
      compliance: [
        "Training on data handling; visible privacy notices; opt‑out for notifications."
      ],
      implementation: [
        "Time to set up: [e.g., \"Go live in 24 hours.\"]",
        "Roles: Owner/manager + front‑desk staff.",
        "Integrations: SMS/email providers (e.g., Twilio for SMS — confirm)."
      ],
      internalLinks: ["features", "faqs"],
      visuals: [
        "'Launch in a day' checklist",
        "Counter photo with QR and tablet"
      ],
      seo: { meta: "Simple setup with screens you own and responsive support. Launch your Virtual Queue fast." }
    },

    faqs: {
      title: "FAQs & Objections",
      purpose: "Answer common questions from owners, ops, and front‑of‑house.",
      hero: {
        headline: "Straight answers for smooth adoption.",
        sub: "From setup to price and privacy, here's what owners ask most.",
        ctaPrimary: "Start free trial",
        ctaSecondary: "Book a demo"
      },
      benefits: [
        "Join on‑site or remotely via smartphone, website, or kiosk.",
        "No app required; printed tickets for non‑mobile users.",
        "No long contracts; monthly subscription."
      ],
      steps: [
        "Customers join digitally or receive a paper ticket.",
        "They get updates on position and wait time.",
        "They're called when it's their turn."
      ],
      useCases: [
        "Transportation, Retail, Healthcare, Government/Public Services"
      ],
      proof: [
        "1‑month free trial available for self‑setup.",
        "SMS example €0.08 via Twilio test service during trial (push is free).",
        "[Stat needed: \"Customer satisfaction ↑ by X% after 4 weeks.\"]",
      ],
      objections: [
        { claim: "Is QR mandatory?", reply: "No. You can hand a position on paper and track in the system." },
        { claim: "Is setup complicated?", reply: "Super simple and easy; team can help remotely." },
        { claim: "Will it work for our size?", reply: "Scales from solo sites to multi‑site chains." }
      ],
      compliance: [
        "Provide privacy policy at join; obtain consent for SMS/email; store only necessary data; allow deletion on request."
      ],
      implementation: [
        "Owner can self‑setup; staff learn in minutes; optional SMS/push/email; cancel anytime — no fees."
      ],
      internalLinks: ["pricing", "implementation"],
      visuals: [
        "FAQ cards",
        "'Ways to join' icons (QR, link, kiosk, paper)"
      ],
      seo: { meta: "Get clear answers about Virtual Queue setup, channels, and costs—no long contracts." }
    },

    "use-cases": {
      title: "Industries & Use Cases",
      purpose: "Show where smartQ delivers value across everyday scenarios.",
      hero: {
        headline: "One simple system—many wins.",
        sub: "Smartq innovative queue will make your business stand out.",
        ctaPrimary: "Start free trial",
        ctaSecondary: "Book a demo"
      },
      benefits: [
        "Retail & click‑and‑collect: smooth pickups, fewer returns lines.",
        "Healthcare: safer, less crowded waiting rooms; reduced stress.",
        "Public services: fair, transparent first‑come‑first‑served.",
        "Transportation desks: organized rebooking/ticketing."
      ],
      steps: [
        "Publish your QR/link on site and online.",
        "Customers join; staff view live demand.",
        "Serve in order and notify the next visitor."
      ],
      useCases: [
        "Retail service counters & returns",
        "Clinics and practices",
        "City hall / licensing offices",
        "Airline/bus ticketing & changes"
      ],
      proof: [
        "Versatile for any business handling queues.",
        "[Stat needed: \"Throughput ↑ by X%; staff idle time ↓ by Y%.\"]",
      ],
      objections: [
        { claim: "Our site is unique.", reply: "smartQ is highly customizable (names, text, logo, languages)." },
        { claim: "We have many services.", reply: "Segment customers into separate queues per service." },
        { claim: "Which industries?", reply: "Transportation, retail, healthcare, public services - anywhere there are queues." }
      ],
      compliance: [
        "Accessibility in public spaces (screen visibility, languages); publish fairness rules."
      ],
      implementation: [
        "Single site or multi‑site; same simple process; optional on‑site display."
      ],
      internalLinks: ["features", "pricing"],
      visuals: [
        "Industry tiles (Retail, Healthcare, Public, Transport)",
        "Queue segmentation diagram"
      ],
      seo: { meta: "See where Virtual Queue shines—retail, healthcare, public services, transport—with a Simple and Easy Queue Management System." }
    },

    pricing: {
      title: "Pricing",
      purpose: "Make value obvious with clear plans, free trial, no long contracts.",
      hero: {
        headline: "Lowest price. Highest profitability.",
        sub: "The profitable queue. Start free, launch fast, and scale when ready—no contract.",
        ctaPrimary: "Start free trial",
        ctaSecondary: "Book a demo"
      },
      benefits: [
        "Simple and Easy Queue Management System.",
        "Free Trial — no payment info required.",
        "Instant Setup — simple app store install.",
        "No Contract — commitment‑free.",
        "Fast Adoption — quick user engagement."
      ],
      steps: [
        "Pick a plan and start the trial.",
        "Configure queues and branding.",
        "Go live; upgrade only if you need more."
      ],
      proof: [
        "Solo Site — €9.99 monthly; includes analytics, QR generator, WhatsApp integration, unlimited staff, business support.",
        "Multi‑Site — POA; Custom App — POA.",
        "[Note: FAQ references self‑setup €10/month and SMS via Twilio at €0.08 — align final copy.]"
      ],
      objections: [
        { claim: "Prices look low — what's missing?", reply: "Core features included (analytics, QR, unlimited staff)." },
        { claim: "SMS costs?", reply: "Push is free; SMS can use provider (e.g., Twilio) at listed rates." },
        { claim: "We need custom features.", reply: "See Custom App — built for scale." },
        { claim: "Can we customize?", reply: "Many options available in-app, with additional customization upon request." },
        { claim: "Long-term contracts?", reply: "No. Monthly billing, cancel anytime for maximum flexibility." }
      ],
      compliance: [
        "Be transparent about third‑party messaging costs; show data retention and cancellation terms."
      ],
      implementation: [
        "Free trial; instant setup; cancel any time."
      ],
      internalLinks: ["features", "faqs"],
      visuals: [
        "Plan cards side‑by‑side; note optional SMS costs"
      ],
      seo: { meta: "Simple pricing for Virtual Queue: free trial, no contract, instant setup—scale from solo site to custom multi‑site." }
    }
  },

  checklist: [
    "No redundancy across pages; coherent story; keywords used naturally.",
    "CTA consistency: \"Start free trial\" primary; \"Book a demo\" secondary.",
    "Terminology consistent: Virtual Queue; Simple and Easy Queue Management System; Waiting without queuing; The best way of queueing.",
    "Analogies used: 'take a ticket and wander freely' where relevant.",
    "Stats placeholders inserted; real pricing/plan data aligned across pages.",
    "Compliance notes: privacy, consent, accessibility, fairness.",
    "Internal links wired among Benefits, Features, Pricing, Implementation."
  ],

  openQuestions: [
    "Pricing alignment: Solo €9.99 vs €10 self‑setup — choose one canonical set and currency.",
    "Free trial length & conditions (FAQ says 1 month) — confirm.",
    "Messaging provider: confirm default SMS vendor and regional rates; clarify push/email costs.",
    "GDPR specifics: data retention period, DPA availability, contact for privacy requests.",
    "Logo, color palette, typography locks; provide assets.",
    "Industries to feature first on Use Cases page.",
    "Setup time promise (e.g., 'Go live in 24 hours').",
    "Support channels & SLA (email/phone; response times).",
    "Testimonials/logos (if available) for trust badges.",
    "Legal: Terms of Service and Privacy links for footer."
  ]
};

// Helper function to process markdown-like bold text and line breaks
export function processMarkdown(text: string): string {
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n/g, '<br>');
}

// Navigation structure
export const navigation = {
  main: [
    { name: 'Benefits', href: '/benefits' },
    { name: 'Features', href: '/features' },
    { name: 'Journey', href: '/journey' },
    { name: 'Implementation', href: '/implementation' },
    { name: 'Pricing', href: '/pricing' },
    { name: 'FAQs', href: '/faqs' },
  ],
  cta: [
    { name: 'Start free trial', href: '/signup', primary: true },
    { name: 'Book a demo', href: '/demo', primary: false },
  ],
  footer: [
    { name: 'Privacy', href: '/privacy' },
    { name: 'Terms', href: '/terms' },
    { name: 'Contact', href: 'mailto:contact@smartq.prod' },
  ]
};

// Site constants
export const siteConfig = {
  name: 'smartQ',
  description: 'Virtual Queue Management - The best way of queueing',
  url: 'https://smartq.pro',
  ogImage: 'https://smartq.pro/social/og.jpg',
  links: {
    twitter: 'https://twitter.com/smartqueue',
    github: 'https://github.com/smartqueue',
  },
};


// Footer sections
export const footerSections = {
  product: 'Product',
  company: 'Company',
  legal: 'Legal'
};
